import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { toDataSourceRequestString } from "@progress/kendo-data-query";
import { map } from "@progress/kendo-data-query/dist/npm/transducers";
import { Button } from "@progress/kendo-react-buttons";
import { SvgIcon } from "@progress/kendo-react-common";
import { DatePicker, TimePicker } from "@progress/kendo-react-dateinputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Switch, TextBox, TextBoxProps } from "@progress/kendo-react-inputs";
import { useLocalization } from "@progress/kendo-react-intl";
import { Avatar } from "@progress/kendo-react-layout";
import { ListView, ListViewEvent, ListViewFooter, ListViewHeader, ListViewItemProps } from "@progress/kendo-react-listview";
import { arrowRotateCwIcon, chartScatterSmoothLinesMarkersIcon, searchIcon, trashIcon } from "@progress/kendo-svg-icons";
import axios from "axios";
import { useState, useEffect, useRef } from "react";
import { TransformComponent, TransformWrapper, useControls } from "react-zoom-pan-pinch";
import { AccessToken } from "../../../config/AccessToken";
import { loadingPanel } from "../../LoadingPanel";
import { NotificationGroup, Notification } from "@progress/kendo-react-notification";
import { Fade } from "@progress/kendo-react-animation";
import { currentLocation } from "../../../assets/current-location";

/*************  ✨ Codeium Command ⭐  *************/
/**
 * HeatMap component is responsible for displaying a heatmap analysis of device activity.
 * It fetches and manages device data, handles user interactions for filtering and analyzing
 * data over selected time intervals, and renders a visual representation of the heatmap.
 * 
 * - Utilizes various states to manage device lists, selected devices, date/time ranges, and
 *   heatmap display settings.
 * - Interacts with APIs to fetch device data and heatmap zones based on user selections.
 * - Provides controls for toggling between advanced and simple time selection modes and for
 *   analyzing selected data.
 * - Renders device lists and a heatmap using canvas elements, supporting zoom and pan interactions.

 */


/******  11fc1854-6d5e-4573-9660-9452fdf42b4c  *******/ 


export const HeatMap = () => {

    const SERVER_URL = process.env.REACT_APP_SERVER_URL;
    const localizationService = useLocalization();
    const [instance, accessTokenRequest] = AccessToken();
    let languageId = localStorage.getItem('languageId');

    const [loading, setLoading] = useState(false);

    const [devicesList, setDevicesList] = useState<any>(null);
    const [counterActiveDevices, setCounterActiveDevices] = useState(null);
    const [counterInactiveDevices, setCounterInactiveDevices] = useState(null);

    const [deviceMacSelected, setDeviceMacSelected] = useState('');
    const [deviceTypeName, setDeviceTypeName] = useState('');
    const [deviceNameSelected, setDeviceNameSelected] = useState<TextBoxProps["value"]>('');

    const [listViewData, setListViewData] = useState(devicesList);
    const [groupsList, setGroupsList] = useState<any>([]);
    const [groupSelected, setGroupSelected] = useState<any>(null);

    const [checked, setChecked] = useState(false);
    const [analyzeEnabled, setAnalyzeEnabled] = useState(false);

    const [zonesList, setZonesList] = useState<any>([]);

    const [stateTime, setStateTime] = useState<any>({
        fromHour: new Date(),
        toHour: new Date()
    });

    const [stateDate, setStateDate] = useState<any>({
        fromDate: new Date(),
        toDate: new Date()
    });

    const [intervalTime, setIntervalTime] = useState<any>(null);
    const [heatMapMessage, setHeatMapMessage] = useState<any>(null);
    

    const intervalTimeList = {
        "data": [
            {
                "id": 0,
                "name": localizationService.toLanguageString('configuration.analysis.heatmap.last_1_hour', 'NOT_FOUND'),
                "value": 60
            },
            {
                "id": 1,
                "name": localizationService.toLanguageString('configuration.analysis.heatmap.last_3_hours', 'NOT_FOUND'),
                "value": 180
            },
            {
                "id": 2,
                "name": localizationService.toLanguageString('configuration.analysis.heatmap.last_24_hours', 'NOT_FOUND'),
                "value": 1440
            },
            {
                "id": 3,
                "name": localizationService.toLanguageString('configuration.analysis.heatmap.today', 'NOT_FOUND'),
                "value": 0
            },
            {
                "id": 4,
                "name": localizationService.toLanguageString('configuration.analysis.heatmap.from_yesterday', 'NOT_FOUND'),
                "value": 1
            },
            {
                "id": 5,
                "name": localizationService.toLanguageString('configuration.analysis.heatmap.last_3_days', 'NOT_FOUND'),
                "value": 4320
            },
        ]
    }


    const [error, setError] = useState('');

    const [currentMapCanvas, setCurrentMapCanvas] = useState<any>([]);
    
    //const [firstMapSelected, setFirstMapSelected] = useState<any>('');
    const [mapGeorefenced, setMapGeoreferenced] = useState(false);
    const [mapsList, setMapsList] = useState([]);

    const [ currentImageMapCanvas, setCurrentImageMapCanvas ] = useState<any>()
    const [ mapWidth, setMapWidth ] = useState(0)
    const [ mapHeight, setMapHeight ] = useState(0)
    
    const mapCanvasRef: any = useRef(null)
    const zoneCanvasRef: any = useRef(null);
    let mapCtx = currentMapCanvas.length > 0 ? currentMapCanvas?.current?.getContext("2d") : mapCanvasRef?.current?.getContext("2d");

    const getDeviceList = async (flag: boolean) => {

        setLoading(true);

        const commonOperations = (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            axios.get(`${SERVER_URL}/device/active`, {
                headers,
                params: {
                    enabled: flag
                }
            })
                .then((res) => {
                    const data: any = res?.data
                    if (flag) {
                        setCounterActiveDevices(data?.data?.length);
                        setDevicesList(data?.data);
                        //setDevicesList(data?.data.splice(0, 12));
                    } else {
                        setCounterInactiveDevices(data?.data?.length);
                    }
                    setLoading(false);
                }).catch((data) => {
                    setError(data?.message);
                });
        }

        if (sessionStorage.getItem('accessToken') != null) {
            let accessToken = sessionStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                // Acquire token silent success
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
    }

    //  call API on page load
    useEffect(() => {
        getDeviceList(true)
        getDeviceList(false)
    }, [])

    const onFilterChange = (ev: any) => {
        let value = ev.value;
        let newData = devicesList.filter((device: any) => {
            let match = false;
            for (const property in device) {
                if (
                    device[property]?.toString() &&
                    device[property]
                        ?.toString()
                        .toLocaleLowerCase()
                        .indexOf(value.toLocaleLowerCase()) >= 0
                ) {
                    match = true;
                }
            }
            return match;
        });
        setListViewData(newData);
    };

    const MyHeader = () => {
        return (
            <>
                <ListViewHeader
                    style={{ color: "#018CA5", fontSize: 14 }}
                    className="pl-3 pb-2 pt-2"
                >
                    {localizationService.toLanguageString('configuration.devices.devices_list', 'NOT FOUND')} ({counterActiveDevices} {localizationService.toLanguageString('configuration.devices.active_devices', 'NOT_FOUND')})
                </ListViewHeader>
            </>
        );
    };

    const MyFooter = () => {
        let messages = 0;
        devicesList.map((i: any) => {
            messages = messages + i.messages;
        });
        return (
            <ListViewFooter
                style={{ color: "#018CA5", fontSize: 14 }}
                className="pl-3 pb-2 pt-2"
            >
                {/*{counterActiveDevices} {localizationService.toLanguageString('configuration.devices.active_devices', 'NOT_FOUND')}*/}
            </ListViewFooter>
        );
    };

    const DevicesRender = (props: ListViewItemProps) => {
        let device = props.dataItem;
        return (
            <div
                className="k-listview-item row p-2 border-bottom align-middle"
                style={{ margin: 0 }}
            >
                <div
                    className="col-9"
                    style={{ cursor: 'pointer', color: "#018CA5" }}
                    onClick={() => { getDeviceMacSelected(device) }}
                >
                    <h2
                        className="device-management-name-h2"
                        style={{
                            fontSize: 16,
                            cursor: 'pointer',
                            padding: '10px',
                            textTransform: 'uppercase'

                        }}
                    >
                        {device.name} ({device.mac})
                    </h2>
                </div>
            </div>
        );
    };

    const scrollHandler = (event: ListViewEvent) => {
        const e = event.nativeEvent;
        if (e.target.scrollTop + 10 >= e.target.scrollHeight - e.target.clientHeight) {
            const moreData = devicesList.splice(0, 6);
            if (moreData.length > 0) {
                setDevicesList(devicesList.concat(moreData));
            }
        }
    };

    const onChangeIntervalTime = (ev: any) => {

        const now = new Date();
        const midnightToday = new Date().setHours(0, 0, 0, 0)
        const midnightYesterday = midnightToday - 24 * 60 * 60 * 1000
        const timeAgo:any = 
            ev.value.id == 3 ? new Date(midnightToday - now.getTimezoneOffset() * 60000) 
            : ev.value.id == 4 ? new Date(midnightYesterday - now.getTimezoneOffset() * 60000) 
            : new Date(now.getTime() - ev.value.value * 60 * 1000 - now.getTimezoneOffset() * 60000);
      
        const formattedDate = timeAgo.toISOString();      
      
        //set the startdate of the interval time
        setIntervalTime(formattedDate);
    }

    const handleToggleSwitch = (event: any) => {
        setChecked(event.target.value);
        //console.log(event.target.value);
    };

    const handleChangeDate = (event: any) => {
        let name = event.target.name || event.target.element.current.name;
        setStateDate({
            ...stateDate,
            [name]: event.target.value,
        });
    };

    const handleChangeTime = (event: any) => {
        let name = event.target.name || event.target.element.current.name;
        setStateTime({
            ...stateTime,
            [name]: event.target.value,
        });
    };

    // called on click device
    const getDeviceMacSelected = (item: any) => {
        setDeviceMacSelected(item.mac);
        setDeviceNameSelected(item.name);
        setDeviceTypeName(item.deviceTypeName);

        //getHeatMapZones(item.mac)
    }
    
    const getHeatMapZones = async(deviceMac:any) => {
        setLoading(true);

        const now = new Date();
        const currentDate = new Date(now.getTime() - now.getTimezoneOffset() * 60000);

        const partsStartDate = new Date(stateDate.fromDate).toISOString().split('T');
        const partsEndDate = new Date(stateDate.toDate).toISOString().split('T');

        const startTime = new Date(stateTime.fromHour);
        startTime.setHours(startTime.getHours() + 1)
        const partsStartTime = startTime.toISOString().split('T');

        const endTime = new Date(stateTime.toHour);
        endTime.setHours(endTime.getHours() + 1)
        const partsEndTime = endTime.toISOString().split('T');

        const startDateConverted = `${partsStartDate[0]}T${partsStartTime[1]}`;
        const endDateConverted = `${partsEndDate[0]}T${partsEndTime[1]}`;

        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            
            const params = {
                startDate: checked ? startDateConverted : intervalTime,
                endDate: checked ? endDateConverted : currentDate.toISOString()
            };
            
            axios.post(`${SERVER_URL}/wakeup/${deviceMac}/logs`, params, { headers })
            .then((response: any) => {

                const zones: any = response?.data?.data?.wakeUpLogs
                const mapId: any = response?.data?.data?.mapId

                let zonesList: any = []
                zones?.forEach((zone: any) => {
                    zonesList.push(zone)
                });
                setZonesList(zonesList)

                getMapById(mapId, zonesList);

                setHeatMapMessage(true);

                setTimeout(() => {
                    setHeatMapMessage(false);
                }, 3000);

                setLoading(false)
            }).catch((data) => {
                //setErrorMessage(error);
                //if (error) {
                //    setTimeout(() => {
                //        setError(false);
                //    }, 3000);
                //}
                setLoading(false)
            })
        }

        if (sessionStorage.getItem('accessToken') != null) {
            let accessToken = sessionStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then(async (accessTokenResponse: any) => {
                // Acquire token silent success
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken)
            })
        }
    }

    const getMapById = async (mapId:any, zonesList:any) => {

        setLoading(true);

        const commonOperations = (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            axios.get(`${SERVER_URL}/map/${mapId}`, { headers })
                .then((res) => {
                    const mapSelected = res?.data.data

                    //renderMap(mapSelected, zonesList)
                    testRenderMap(mapSelected, zonesList)

                    //setFirstMapSelected(mapSelected)

                    setLoading(false);
                })
                .catch((error) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    setLoading(false);
                });
        }

        if (sessionStorage.getItem('accessToken') != null) {
            let accessToken = sessionStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
    }

    const testRenderMap = (map: any, zonesList?:any) => {
        const mapCanvas = mapCanvasRef.current;
        const zoneCanvas = zoneCanvasRef.current;
        
        const image = new Image();
        image.src = `data:image/jpeg;base64, ${map.image}`;   
        
        image.onload = () => {
            mapCanvas.width = zoneCanvas.width = image.naturalWidth;
            mapCanvas.height = zoneCanvas.height = image.naturalHeight;
            
            const mapCtx = mapCanvas.getContext('2d');
            mapCtx.drawImage(image, 0, 0);
            
            const zoneCtx = zoneCanvas?.getContext("2d");

            // Create a color gradient
            //const gradient = zoneCtx.createRadialGradient(0, 0, 0, 0, 0, 10);
            //gradient.addColorStop(0, 'green');
            //gradient.addColorStop(1, 'red');

            mapCtx.transform(1, 0, 0, -1, 0, mapCanvasRef.height)

                // Invert the Y-axis by translating the origin and scaling
                zoneCtx.translate(0, zoneCanvas.height); // Move the origin to the bottom-left corner
                zoneCtx.scale(1, -1); // Invert the Y-axis

                
                zonesList.forEach((zone: any) => {
                    zoneCtx.beginPath();
                    
                    //zoneCtx.fillStyle = gradient   
                    //zoneCtx.fillStyle = '#ff0000'
                    zoneCtx.fillStyle = '#'+zone.position.color   
                        
                    //zoneCtx.beginPath();
                    //zoneCtx.fillRect(zone.longitude, zone.latitude, 10, 10);
                    zoneCtx.arc(zone.position.longitude, zone.position.latitude, 5, 0, 2 * Math.PI);

                    zoneCtx.globalAlpha = 1;
                    zoneCtx.fill();
                })
        };
    }

    const renderMap = (map: any, zonesList?:any) => {

        //if (map?.georeferenced == 0 && mapCanvas?.current != null) {
            //mapCtx?.clearRect(0, 0, mapCtx?.canvas.width, mapCtx?.canvas.height)

            const zoneCtx = zoneCanvasRef?.current?.getContext("2d");
            //zoneCtx?.clearRect(0, 0, zoneCanvas.current?.width, zoneCanvas.current?.height)

            let width: any = 0

            mapCtx = mapCanvasRef.current?.getContext("2d");
            width = mapCtx?.canvas.parentElement.offsetWidth;

            let imageMapCanvas:any = new Image();

            
            imageMapCanvas.src = `data:image/jpeg;base64, ${map.image}`;   

            setCurrentImageMapCanvas(imageMapCanvas)

            const canvas = mapCanvasRef.current;
            
            imageMapCanvas.onload = () => {
                
                setMapWidth(imageMapCanvas.width)
                setMapHeight(imageMapCanvas.height)

                let wImg = imageMapCanvas.width;
                let hImg = imageMapCanvas.height;
                let height = width * hImg / wImg;
                mapCanvasRef.width = zoneCanvasRef.width = 600 //width;
                mapCanvasRef.height = zoneCanvasRef.height = 300 //height;
                mapCanvasRef.current.style.backgroundImage = 'url(' + imageMapCanvas.src + ')';

                canvas.width = imageMapCanvas.width;
                canvas.height = imageMapCanvas.height;
                //mapCtx.drawImage(imageMapCanvas, 0, 0);

                mapCtx.drawImage(imageMapCanvas, 0, 0, mapCanvasRef.width, mapCanvasRef.height);
                 
                mapCtx.transform(1, 0, 0, -1, 0, mapCanvasRef.height)

                // Invert the Y-axis by translating the origin and scaling
                zoneCtx.translate(0, zoneCanvasRef.height); // Move the origin to the bottom-left corner
                zoneCtx.scale(1, -1); // Invert the Y-axis
                
                //zoneCtx.globalCompositeOperation = 'multiply';

                
                zonesList.forEach((zone: any, id:any) => {
                    
                    zoneCtx.beginPath();
                    //zoneCtx.fillStyle = id % 2 == 0 ? 'green' : 'red'   
                    zoneCtx.fillStyle = 'red'   
                        
                    zoneCtx.beginPath();
                    zoneCtx.fillRect(zone.longitude, zone.latitude, 10, 10);
                    
                    //zoneCtx.arc(zone.longitude, zone.latitude, 3, 0, 2 * Math.PI);

                    zoneCtx.fill();
                })
            };
        //    setMapGeoreferenced(false)
        //} else {
        //    setMapGeoreferenced(true)
        //}
    }
    
    const analyze = () => {

        setAnalyzeEnabled(true);
        getHeatMapZones(deviceMacSelected);

    } 

    const Controls = () => {
        const { zoomIn, zoomOut, resetTransform } = useControls();

        return (
            //<div className="k-button-subscription">
            <div className="k-button" style={{ border: 'none'}}>
                <button 
                    style={{
                    position: 'absolute',
                    width: '32px',
                    height: '32px',
                    zIndex: 10,
                    top: '0px',
                    left: '10px',
                    backgroundColor: 'white',
                    border: '1px solid #ccc',
                    cursor: 'pointer'
                    }}
                    title={localizationService.toLanguageString('map.zoom_in', 'NOT_FOUND')}
                    onClick={() => zoomIn()}>+</button>
                <button 
                    style={{
                    position: 'absolute',
                    width: '32px',
                    height: '32px',
                    zIndex: 10,
                    top: '0px',
                    left: '47px',
                    backgroundColor: 'white',
                    border: '1px solid #ccc',
                    cursor: 'pointer'
                    }} 
                    title={localizationService.toLanguageString('map.zoom_out', 'NOT_FOUND')}
                    onClick={() => zoomOut()}>-</button>
                <button 
                    style={{
                    position: 'absolute',
                    width: '32px',
                    height: '32px',
                    zIndex: 10,
                    top: '0px',
                    left: '84px',
                    backgroundColor: 'white',
                    border: '1px solid #ccc',
                    cursor: 'pointer'
                    }}   
                    title={localizationService.toLanguageString('map.reset_zoom', 'NOT_FOUND')}
                    onClick={() => resetTransform()}>
                    <SvgIcon icon={currentLocation} />
                </button>
            </div>
        );
    };

    return (
        <>
            {loading && loadingPanel}
            {devicesList?.length > 0 &&
                <div 
                    style={{ 
                        display: 'inline-flex',
                        width: '100%',
                        height: '100%'
                    }}
                >
                    <NotificationGroup
                        style={{
                            right: 0,
                            bottom: 50,
                            alignItems: "flex-start",
                            flexWrap: "wrap-reverse",
                        }}
                    >
                        <Fade enter={true} exit={true}>
                            {heatMapMessage && (
                                <Notification
                                    type={{ style: "success", icon: true }}
                                    closable={true}
                                    onClose={() => setHeatMapMessage(false)}
                                >
                                    <span>{localizationService.toLanguageString('configuration.analysis.heatmap.map_loaded', 'NOT FOUND')}</span>
                                </Notification>
                            )}
                        </Fade>
                    </NotificationGroup>

                    {/* left panel */}
                    <div className="flex-center-child-element" style={{ background: "#F7F7F7", padding: "0", width: "auto" }}>
                        {/*<div className="k-listview-item row p-1 align-middle" style={{ display: 'block', margin: 0 }}>*/}
                        <div
                            className="k-listview-item row p-2 align-middle"
                            style={{
                                //display: 'block',
                                margin: 0
                            }}
                        >
                            <fieldset style={{ display: 'flex' }} >
                                {/*{localizationService.toLanguageString('search', 'NOT FOUND')}:*/}
                                <TextBox
                                    style={{
                                        border: '2px solid #ccc',
                                        boxShadow: 'inset 0px 0px 5px 0px rgba(0,0,0,0.13)',
                                        marginBottom: '10px',
                                    }}
                                    //value={deviceMacSelected ?? null}
                                    onChange={onFilterChange}
                                    placeholder={localizationService.toLanguageString('configuration.analysis.heatmap.search_device', 'NOT FOUND')}
                                    prefix={() => (
                                        <SvgIcon icon={searchIcon} svgStyle={{ margin: '20px -5px 0px 5px' }} />
                                    )}
                                />

                                {/* refresh device list button */}
                                <Button
                                    className="plusParam"
                                    style={{ marginTop: '0px' }}
                                    onClick={() => { getDeviceList(true) }}
                                    title={localizationService.toLanguageString('configuration.analysis.heatmap.refresh_list', 'NOT FOUND')}
                                >
                                    <Avatar
                                        className="plusParam"
                                    >
                                        <SvgIcon icon={arrowRotateCwIcon} style={{ margin: '8px' }} />
                                    </Avatar>
                                </Button>

                            </fieldset>
                        </div>

                        <ListView
                            data={listViewData ?? devicesList}
                            item={DevicesRender}
                            style={{ background: "#F7F7F7", height: '85%' }}
                            header={MyHeader}
                            footer={MyFooter}
                            onScroll={scrollHandler}
                        />
                    </div>

                    {/* right panel */}
                    <div 
                        className="flex-left-child-element" 
                        style={{ display: 'inline', paddingLeft: '35px', background: "white", overflow: 'auto' }}
                    >
                        <div 
                            style={{ display: 'flex', marginTop: '10px', alignItems: 'center' }}>
                            <p style={{ margin: '0px 10px 0px 0px' }}>
                                {localizationService.toLanguageString('configuration.analysis.heatmap.advanced', 'NOT_FOUND')}
                            </p>
                                                        
                            <Switch
                                //onLabel="Advanced"
                                onChange={handleToggleSwitch}
                                checked={checked}
                            />
                            
                            
                        </div>
                        
                        <br />

                        {deviceMacSelected != '' &&
                            <p style={{ margin: '0px 10px 20px 0px' }}>
                                {localizationService.toLanguageString('configuration.analysis.heatmap.device_selected', 'NOT FOUND')}: {deviceMacSelected}
                            </p>
                        }

                        {
                            //advanced timeslot
                            checked ?
                                <div style={{ display: 'grid', marginTop: '10px' }}>
                                    <div>
                                        <DatePicker
                                            name="fromDate"
                                            validityStyles={false}
                                            label={localizationService.toLanguageString('configuration.analysis.heatmap.from_date', 'NOT FOUND')}
                                            //defaultValue={defaultValue}  
                                            value={stateDate.fromDate}
                                            width={150}
                                            //min={min}
                                            //max={max}
                                            required={true}
                                            onChange={handleChangeDate}
                                            validationMessage={
                                                stateDate.fromDate === null
                                                    ? `${localizationService.toLanguageString('configuration.analysis.heatmap.from_date', 'NOT FOUND')}`
                                                    : ""
                                            }
                                        />

                                        <DatePicker
                                            name="toDate"
                                            style={{ marginLeft: '20px' }}
                                            validityStyles={false}
                                            label={localizationService.toLanguageString('configuration.analysis.heatmap.to_date', 'NOT FOUND')}
                                            //defaultValue={defaultValue}  
                                            value={stateDate.toDate}
                                            width={150}
                                            //min={min}
                                            //max={max}
                                            required={true}
                                            onChange={handleChangeDate}
                                            validationMessage={
                                                stateDate.toDate === null
                                                    ? `${localizationService.toLanguageString('configuration.analysis.heatmap.to_date', 'NOT FOUND')}`
                                                    : ""
                                            }
                                        />
                                    </div>

                                    <div style={{ display: 'flex', marginTop: '10px' }}>
                                        <TimePicker
                                            format="HH:mm:ss"
                                            name="fromHour"
                                            validityStyles={false}
                                            label={localizationService.toLanguageString('configuration.analysis.heatmap.from_hour', 'NOT FOUND')}
                                            //defaultValue={defaultValue}  
                                            value={stateTime.fromHour}
                                            width={150}
                                            //min={min}
                                            //max={max}
                                            required={true}
                                            onChange={handleChangeTime}
                                            validationMessage={
                                                stateTime.fromHour === null
                                                    ? `${localizationService.toLanguageString('configuration.analysis.heatmap.from_hour', 'NOT FOUND')}`
                                                    : ""
                                            }
                                        />
                                        <div style={{ marginLeft: '20px' }}>
                                            <TimePicker
                                                format="HH:mm:ss"
                                                name="toHour"                                            
                                                validityStyles={false}
                                                label={localizationService.toLanguageString('configuration.analysis.heatmap.to_hour', 'NOT FOUND')}
                                                //defaultValue={defaultValue}  
                                                value={stateTime.toHour}
                                                width={150}
                                                //min={min}
                                                //max={max}
                                                required={true}
                                                valid={
                                                    stateTime.fromHour !== null &&
                                                    stateTime.toHour !== null &&
                                                    stateTime.fromHour.getTime() < stateTime.toHour.getTime()
                                                }
                                                onChange={handleChangeTime}
                                                validationMessage={
                                                    stateTime.toHour === null
                                                        ? `${localizationService.toLanguageString('configuration.analysis.heatmap.to_hour', 'NOT FOUND')}`
                                                        : `${localizationService.toLanguageString('configuration.analysis.heatmap.to_hour', 'NOT FOUND')}`
                                                }
                                            />
                                        </div>
                                    </div>
                                    <Button
                                        title={localizationService.toLanguageString('configuration.analysis.heatmap.analyze', 'NOT FOUND')}
                                        className='analysis'
                                        disabled={!deviceMacSelected} 
                                        onClick={analyze}
                                    >
                                        <Avatar
                                            className="analysis"
                                        >
                                            <SvgIcon icon={chartScatterSmoothLinesMarkersIcon} />
                                        </Avatar>
                                    </Button>
                                </div>
                                :
                                <div style={{ display: 'grid' }}>
                                    <div style={{ fontSize: '14px', margin: '0px 10px 0px 0px' }}>{localizationService.toLanguageString('configuration.analysis.heatmap.interval_time', 'NOT_FOUND')}</div>
                                    <DropDownList
                                        //defaultItem={localizationService.toLanguageString('configuration.analysis.heatmap.interval_time', 'NOT FOUND')}
                                        style={{
                                            width: "220px"
                                        }}
                                        data={intervalTimeList.data}
                                        textField="name"
                                        onChange={onChangeIntervalTime}
                                    />
                                    <Button
                                        title={localizationService.toLanguageString('configuration.analysis.heatmap.analyze', 'NOT FOUND')}
                                        className='analysis'
                                        disabled={!deviceMacSelected} 
                                        onClick={analyze}
                                    >
                                        <Avatar
                                            className="analysis"
                                        >
                                            <SvgIcon icon={chartScatterSmoothLinesMarkersIcon} />
                                        </Avatar>
                                    </Button>
                                </div>
                        }      
                                          

                        {/*map*/}
                        <div
                            style={{ 
                                display: `${ analyzeEnabled ? 'block' : 'none'}`,
                                marginTop: '10px'
                            }}
                        >                            
                            <TransformWrapper
                                initialScale={0.2}
                                initialPositionX={0}
                                initialPositionY={0}
                                minScale={0.2}
                                centerOnInit={true}
                                centerZoomedOut={true}
                                limitToBounds={true}
                                smooth={true}
                                //disablePadding={true}
                                //disabled={true}
                                //panning={{ disabled: true }} // disabilita lo spostamento del canvas
                            >
                                {({ zoomIn, zoomOut, resetTransform, centerView, zoomToElement, ...rest }) => (
                                    <>
                                        <Controls />
                                        <TransformComponent
                                            wrapperStyle={{
                                                width: '100%',
                                                height: '100%'
                                            }}
                                            //contentStyle={{
                                            //    width: '600px',
                                            //    height: '300px',
                                            //}}
                                        >
                                            <canvas
                                                ref={mapCanvasRef}
                                                className="mapCanvasHeatMap"
                                                //width={600}
                                                //height={300}
                                            />
                                            <canvas
                                                ref={zoneCanvasRef}
                                                className="zoneCanvas"
                                                //width={600}
                                                //height={300}
                                            />
                                        </TransformComponent>
                                    </>
                                )}
                            </TransformWrapper>
                            
                        </div>

                    </div>
                </div>
            }
        </>
    )
}